/**
 * ═══════════════════════════════════════════════════════════════════════════════
 * SERVICE : Cal Event Mappings
 * ═══════════════════════════════════════════════════════════════════════════════
 * 
 * Ce service gère la récupération des configurations Cal.com depuis la table
 * `cal_event_mappings`, qui contient tous les slugs Cal.com pour chaque produit.
 * 
 * STRUCTURE DE LA TABLE cal_event_mappings :
 * - product_id : UUID du produit (FK vers products)
 * - location_id : UUID de la localisation (FK vers locations, nullable)
 * - cal_team_slug : Slug de l'équipe Cal.com (ex: "prsnl")
 * - first_timer_shooting_slug : Slug pour shooting first-timer (round-robin)
 * - first_timer_shooting_id : ID numérique Cal.com pour shooting first-timer
 * - first_timer_preparation_slug : Slug pour préparation first-timer (round-robin)
 * - first_timer_preparation_id : ID numérique Cal.com pour préparation first-timer
 * - managed_shooting_slug : Slug pour shooting recurring
 * - managed_shooting_id : ID numérique Cal.com pour shooting recurring
 * - managed_preparation_slug : Slug pour préparation recurring
 * - managed_preparation_id : ID numérique Cal.com pour préparation recurring
 * - is_active : Flag pour activer/désactiver une config
 * 
 * LOGIQUE DE FALLBACK :
 * 1. Si locationId fourni → cherche config spécifique à cette location
 * 2. Si pas trouvée → fallback sur config générique (location_id = null)
 * 
 * Cette approche permet d'avoir :
 * - Une config par défaut pour chaque produit (location_id = null)
 * - Des overrides spécifiques pour certaines locations si nécessaire
 * 
 * ═══════════════════════════════════════════════════════════════════════════════
 */

import { supabase } from "@/integrations/supabase/client";
import type { Tables } from "@/integrations/supabase/types";

/**
 * Type représentant une configuration Cal.com complète pour un produit
 * Contient tous les slugs nécessaires pour bookings first-timer
 */
export type ProductCalConfig = Tables<"cal_event_mappings">;

/**
 * Type pour les événements Cal.com supportés
 */
export type CalEventType = 'shooting' | 'preparation';


/**
 * Récupère la configuration Cal.com pour un produit
 * 
 * LOGIQUE :
 * 1. Si locationId fourni :
 *    - Cherche config avec product_id + location_id match
 *    - Si trouvée → retourne cette config spécifique
 * 2. Fallback sur config générique :
 *    - Cherche config avec product_id + location_id = null
 * 
 * @param productId - UUID du produit
 * @param locationId - UUID de la localisation (optionnel)
 * @returns Configuration Cal.com ou null si non trouvée
 * 
 * @example
 * // Config générique
 * const config = await getProductCalConfig('prod-123');
 * 
 * @example
 * // Config spécifique à Paris
 * const config = await getProductCalConfig('prod-123', 'location-paris');
 */
export const getProductCalConfig = async (
  productId: string,
  locationId?: string
): Promise<ProductCalConfig | null> => {
  try {
    let locationToUse = locationId;

    if (!locationToUse) {
      const { data: locations, error: locationsError } = await supabase
        .from("locations")
        .select("*")
        .order("is_default", { ascending: false })
        .order("name", { ascending: true });

      if (locationsError) {
        console.error("Error fetching locations for cal config:", locationsError);
        return null;
      }

      locationToUse = locations?.[0]?.id;
    }

    if (locationToUse) {
      const { data: locationSpecific, error: locationError } = await supabase
        .from("cal_event_mappings")
        .select("*")
        .eq("product_id", productId)
        .eq("location_id", locationToUse)
        .eq("is_active", true)
        .maybeSingle();

      if (locationError) {
        console.warn("Error fetching location-specific config:", locationError);
      }

      if (locationSpecific) {
        return locationSpecific;
      }
      
      console.log(`⚠️ No location-specific config found, falling back to generic config`);
    }

    const { data, error } = await supabase
      .from("cal_event_mappings")
      .select("*")
      .eq("product_id", productId)
      .is("location_id", null)
      .eq("is_active", true)
      .maybeSingle();

    if (error) throw error;
    
    if (data) {
      console.log(`✅ Found generic config for product ${productId}`);
    } else {
      console.warn(`⚠️ No config found for product ${productId}`);
    }
    
    return data;
  } catch (error) {
    console.error("Error fetching product cal config:", error);
    return null;
  }
};


/**
 * Récupère toutes les configurations Cal.com actives
 * 
 * Utile pour :
 * - Admin panel pour voir toutes les configs
 * - Debug et monitoring
 * - Export de données
 * 
 * @returns Array de toutes les configurations actives, triées par product_id
 */
export const getAllEventMappings = async (): Promise<ProductCalConfig[]> => {
  try {
    const { data, error } = await supabase
      .from("cal_event_mappings")
      .select("*")
      .eq("is_active", true)
      .order("product_id", { ascending: true });

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error("Error fetching all event mappings:", error);
    return [];
  }
};

/**
 * Récupère la configuration Cal.com d'un produit spécifique
 * 
 * Alias de getProductCalConfig() pour compatibilité avec l'ancien naming
 * 
 * @param productId - UUID du produit
 * @returns Configuration Cal.com générique du produit (sans location spécifique)
 */
export const getEventMappingsByProduct = async (
  productId: string
): Promise<ProductCalConfig | null> => {
  return getProductCalConfig(productId);
};

/**
 * Récupère toutes les configurations Cal.com spécifiques à une localisation
 * 
 * Utile pour :
 * - Afficher tous les produits disponibles dans un studio spécifique
 * - Gérer les overrides de config par location
 * 
 * @param locationId - UUID de la localisation
 * @returns Array de configurations pour cette location
 */
export const getEventMappingsByLocation = async (
  locationId: string
): Promise<ProductCalConfig[]> => {
  try {
    const { data, error } = await supabase
      .from("cal_event_mappings")
      .select("*")
      .eq("location_id", locationId)
      .eq("is_active", true);

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error(`Error fetching event mappings for location ${locationId}:`, error);
    return [];
  }
};
